/**
 * Columns of cards, and moving a card from one column to another by dragging
 * it — with a mouse, a finger, or the keyboard (focus a card, Space, arrow
 * keys, Space).
 *
 * **It does not fetch, and it does not know what a card is.** It takes columns
 * of items, renders each through `renderItem`, and raises `onMove(item, from,
 * to)` when one is dropped on another column. Whether that is allowed, what it
 * calls and what happens on failure are the page's. A page that wants the card
 * to land at once updates its own data before the request — the board only
 * draws what it is given.
 *
 * Built on `@dnd-kit/core`: pointer, touch and keyboard sensors, and screen
 * reader announcements, which are translated here (`common:kanban.*`) rather
 * than left in the library's English.
 *
 * Order *within* a column is the page's, not a drag target: dropping on the
 * column a card came from does nothing.
 */
import { useMemo, useState, type Key, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface KanbanColumn<T> {
  id: Key;
  /** The column's name, as text: it is what a screen reader announces. */
  label: string;
  /** Under the name — a count, a total. */
  summary?: ReactNode;
  items: readonly T[];
  /** Under the last card — "12 more not shown". */
  footer?: ReactNode;
}

export interface KanbanBoardProps<T> {
  columns: readonly KanbanColumn<T>[];
  itemKey: (item: T) => Key;
  /** The card's name, as text, for the announcements. */
  itemLabel: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  /** Shown in a column with no cards. */
  emptyText: string;
  /** False keeps a card where it is — a closed deal, a caller who may not move it. */
  canMove?: (item: T) => boolean;
  /** Dropped on another column. Not called for a drop on its own column, or outside any. */
  onMove: (item: T, fromColumn: Key, toColumn: Key) => void;
  /** Every card stays put — a move in flight, a caller with no right to move. */
  disabled?: boolean;
  columnWidth?: number;
}

const itemId = (key: Key): string => `item:${String(key)}`;
const columnId = (key: Key): string => `column:${String(key)}`;

/** The column under the pointer; with the keyboard there is no pointer, so the one the card overlaps most. */
const collisions: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  return within.length > 0 ? within : rectIntersection(args);
};

function Card({ id, disabled, children }: { id: string; disabled: boolean; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? 'default' : 'grab',
        touchAction: disabled ? 'auto' : 'none',
        borderRadius: 1,
        '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineStyle: 'solid' },
      }}
    >
      {children}
    </Box>
  );
}

function Column({
  id,
  label,
  summary,
  footer,
  width,
  emptyText,
  children,
}: {
  id: string;
  label: string;
  summary: ReactNode;
  footer: ReactNode;
  width: number;
  emptyText: string;
  children: ReactNode[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Paper
      variant="outlined"
      sx={{
        width,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isOver ? 'action.selected' : 'action.hover',
        transition: 'background-color 120ms',
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap title={label}>
          {label}
        </Typography>
        {summary && (
          <Typography variant="caption" color="text.secondary" component="div">
            {summary}
          </Typography>
        )}
      </Box>
      <Stack ref={setNodeRef} spacing={1} sx={{ px: 1, pb: 1, flexGrow: 1, minHeight: 120 }}>
        {children.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ p: 1, textAlign: 'center' }}>
            {emptyText}
          </Typography>
        ) : (
          children
        )}
        {footer}
      </Stack>
    </Paper>
  );
}

export function KanbanBoard<T>({
  columns,
  itemKey,
  itemLabel,
  renderItem,
  emptyText,
  canMove,
  onMove,
  disabled = false,
  columnWidth = 280,
}: KanbanBoardProps<T>) {
  const { t } = useTranslation('common');
  const [active, setActive] = useState<UniqueIdentifier | null>(null);

  // A small distance before a drag starts, so a click on a link inside a card
  // is still a click.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const index = useMemo(() => {
    const items = new Map<UniqueIdentifier, { item: T; column: KanbanColumn<T> }>();
    const byColumn = new Map<UniqueIdentifier, KanbanColumn<T>>();
    for (const column of columns) {
      byColumn.set(columnId(column.id), column);
      for (const item of column.items) items.set(itemId(itemKey(item)), { item, column });
    }
    return { items, byColumn };
  }, [columns, itemKey]);

  const labelOf = (id: UniqueIdentifier | undefined): string => {
    if (id === undefined) return '';
    const found = index.items.get(id);
    return found ? itemLabel(found.item) : '';
  };
  const columnLabelOf = (id: UniqueIdentifier | undefined): string =>
    id === undefined ? '' : (index.byColumn.get(id)?.label ?? '');

  const announcements: Announcements = {
    onDragStart: ({ active: dragged }) => t('kanban.picked', { item: labelOf(dragged.id) }),
    onDragOver: ({ active: dragged, over }) =>
      over
        ? t('kanban.over', { item: labelOf(dragged.id), column: columnLabelOf(over.id) })
        : t('kanban.overNone', { item: labelOf(dragged.id) }),
    onDragEnd: ({ active: dragged, over }) =>
      over
        ? t('kanban.dropped', { item: labelOf(dragged.id), column: columnLabelOf(over.id) })
        : t('kanban.cancelled', { item: labelOf(dragged.id) }),
    onDragCancel: ({ active: dragged }) => t('kanban.cancelled', { item: labelOf(dragged.id) }),
  };

  function handleEnd(event: DragEndEvent) {
    setActive(null);
    const found = index.items.get(event.active.id);
    const target = event.over ? index.byColumn.get(event.over.id) : undefined;
    if (!found || !target || target.id === found.column.id) return;
    onMove(found.item, found.column.id, target.id);
  }

  const dragged = active === null ? undefined : index.items.get(active);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisions}
      accessibility={{ announcements, screenReaderInstructions: { draggable: t('kanban.instructions') } }}
      onDragStart={(event: DragStartEvent) => setActive(event.active.id)}
      onDragCancel={() => setActive(null)}
      onDragEnd={handleEnd}
    >
      <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, alignItems: 'stretch' }}>
        {columns.map((column) => (
          <Column
            key={column.id}
            id={columnId(column.id)}
            label={column.label}
            summary={column.summary}
            footer={column.footer}
            width={columnWidth}
            emptyText={emptyText}
          >
            {column.items.map((item) => (
              <Card
                key={itemKey(item)}
                id={itemId(itemKey(item))}
                disabled={disabled || canMove?.(item) === false}
              >
                {renderItem(item)}
              </Card>
            ))}
          </Column>
        ))}
      </Box>
      <DragOverlay>
        {dragged ? <Box sx={{ width: columnWidth - 16, boxShadow: 6, borderRadius: 1 }}>{renderItem(dragged.item)}</Box> : null}
      </DragOverlay>
    </DndContext>
  );
}
